import type {
  Attributes as AttributesType,
  ClassName,
  DataAttribute,
} from '../../../amplienceTypes/partials/exported/element-attributes-schema';
import type { PropsWithChildren, CSSProperties } from 'react';

import { forwardRef } from 'react';
import styled from 'styled-components';

import { removeUselessSpaces } from '../../../utilities/parsers';

interface AttributesInterface {
  additionalClassNames?: string;
  className?: string;
  ariaRole?: string;
  ariaLabel?: string;
  as?: string;
  dataAttributes?: DataAttribute[];
  classNames?: ClassName[];
  style?: CSSProperties;
}

const S = {
  component: styled.div``,
};

const Attributes = forwardRef<
  HTMLDivElement,
  PropsWithChildren<AttributesType & AttributesInterface>
>((props, ref) => {
  const {
    dataAttributes,
    classNames,
    id,
    additionalClassNames,
    className,
    ariaRole,
    ariaLabel,
    children,
    as = 'div',
    style,
    // remove _meta from props as is not a valid HTML attribute
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    _meta,
    ...rest
  }: PropsWithChildren<AttributesType & AttributesInterface> = props;

  const makeDataAttributes = () => {
    const attributes: { [key: string]: string | boolean } = {};
    const cssStyles: { [key: string]: string } = {};

    dataAttributes?.forEach(attribute => {
      if (attribute.name === 'style' && attribute.value) {
        const styles: string[] = attribute.value
          .trim()
          .split(';')
          .filter(style => style);

        styles.forEach(style => {
          const [property, value] = style.trim().split(':');

          cssStyles[property.trim()] = value.trim();
        });
      } else if (attribute.name === 'hidden') {
        attributes[attribute.name] = attribute.value === 'true';
      } else if (attribute.name && attribute.value) {
        attributes[attribute.name] = attribute.value;
      }
    });

    return { attributes, cssStyles };
  };

  const getIdAttr = () => (id ? { id } : {});

  // CMS-authored `style` data attributes win over caller styles on conflict, so content
  // authors keep the last word; anything the caller sets that they did not author - CSS
  // custom properties, for instance - still comes through.
  const mergedStyle = { ...style, ...makeDataAttributes().cssStyles } as CSSProperties;

  const classes = removeUselessSpaces(
    `${additionalClassNames ?? ''} ${(classNames as ClassName[])?.join(' ') ?? ''} ${
      className ?? ''
    }`
  );

  return (
    <S.component
      {...getIdAttr()}
      {...(classes ? { className: classes } : {})}
      {...makeDataAttributes().attributes}
      {...rest}
      style={mergedStyle}
      role={ariaRole}
      aria-label={ariaLabel}
      ref={ref}
      as={as}
    >
      {children}
    </S.component>
  );
});

Attributes.displayName = 'Attributes';

export default Attributes;
